import * as React from 'react'

const MESSAGE_NAVIGATION_SCROLL_RELEASE_MS = 2600

function scrollMessageIntoContainerView(
  container: HTMLElement,
  messageId: number,
  behavior: ScrollBehavior = 'auto'
): boolean {
  const messageElement = container.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement | null
  if (!messageElement) return false

  const containerRect = container.getBoundingClientRect()
  const messageRect = messageElement.getBoundingClientRect()
  const targetScrollTop =
    container.scrollTop +
    (messageRect.top - containerRect.top) -
    container.clientHeight / 2 +
    messageRect.height / 2

  container.scrollTo({
    top: Math.max(0, targetScrollTop),
    behavior,
  })

  return true
}

function scheduleMessageMediaLoadRecenter(
  container: HTMLElement,
  messageId: number,
  behavior: ScrollBehavior,
  onRecenter: (messageId: number, behavior: ScrollBehavior) => void
) {
  const messageElement = container.querySelector(`[data-message-id="${messageId}"]`)
  if (!messageElement) return

  const mediaElements = Array.from(messageElement.querySelectorAll('img, video'))
  mediaElements.forEach(element => {
    const isAlreadyLoaded = element instanceof HTMLImageElement ? element.complete : false
    if (isAlreadyLoaded) return

    const handleMediaLoad = () => {
      onRecenter(messageId, behavior)
    }

    element.addEventListener('load', handleMediaLoad, { once: true })
    element.addEventListener('loadeddata', handleMediaLoad, { once: true })
    element.addEventListener('error', handleMediaLoad, { once: true })
  })
}

interface ScrollManager {
  messagesEndRef: React.RefObject<HTMLDivElement>
  messagesContainerRef: React.RefObject<HTMLDivElement>
  conversationListRef: React.RefObject<HTMLDivElement>
  scrollToBottom: () => void
  scrollToBottomInstant: () => void
  setSuppressBottomScroll: (suppress: boolean) => void
  beginMessageNavigationScroll: () => void
  isBottomScrollSuppressed: () => boolean
  isNearBottom: (threshold?: number) => boolean
  maintainScrollPosition: () => void
  scrollToMessage: (messageId: number) => void
  scrollToMessageWithRetries: (messageId: number) => void
  scrollToMessageWithDelay: (messageId: number, delay?: number) => void
  scrollToConversation: (conversationId: number, conversationType?: string) => void
  restoreScrollPosition: () => void
  prepareConversationBottomScroll: () => void
  isConversationBottomSnapRequested: () => boolean
  clearConversationBottomSnapRequest: () => void
}

export const useChatScroll = (): ScrollManager => {
  const messagesEndRef = React.useRef<HTMLDivElement>(null)
  const messagesContainerRef = React.useRef<HTMLDivElement>(null)
  const conversationListRef = React.useRef<HTMLDivElement>(null)

  const scrollPositionRef = React.useRef<{
    scrollTop: number
    scrollHeight: number
  } | null>(null)
  const scrollAnchorRef = React.useRef<{
    messageId: number
    offsetTop: number
  } | null>(null)
  const loadPreviousSuppressReleaseRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const suppressBottomScrollRef = React.useRef(false)
  const bottomScrollTimerIdsRef = React.useRef<ReturnType<typeof setTimeout>[]>([])
  const messageNavigationReleaseTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const conversationBottomSnapRequestedRef = React.useRef(false)

  const cancelBottomScrollTimers = React.useCallback(() => {
    bottomScrollTimerIdsRef.current.forEach(timerId => clearTimeout(timerId))
    bottomScrollTimerIdsRef.current = []
  }, [])

  const isBottomScrollSuppressed = React.useCallback(() => suppressBottomScrollRef.current, [])

  const isNearBottom = React.useCallback((threshold = 150) => {
    const container = messagesContainerRef.current
    if (!container) {
      return true
    }

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
    return distanceFromBottom <= threshold
  }, [])

  const releaseMessageNavigationScroll = React.useCallback((delayMs = MESSAGE_NAVIGATION_SCROLL_RELEASE_MS) => {
    if (messageNavigationReleaseTimerRef.current) {
      clearTimeout(messageNavigationReleaseTimerRef.current)
    }

    messageNavigationReleaseTimerRef.current = setTimeout(() => {
      suppressBottomScrollRef.current = false
      messageNavigationReleaseTimerRef.current = null
    }, delayMs)
  }, [])

  const setSuppressBottomScroll = React.useCallback(
    (suppress: boolean) => {
      suppressBottomScrollRef.current = suppress
      if (suppress) {
        cancelBottomScrollTimers()
        if (messageNavigationReleaseTimerRef.current) {
          clearTimeout(messageNavigationReleaseTimerRef.current)
          messageNavigationReleaseTimerRef.current = null
        }
      }
    },
    [cancelBottomScrollTimers]
  )

  const beginMessageNavigationScroll = React.useCallback(() => {
    setSuppressBottomScroll(true)
    releaseMessageNavigationScroll()
  }, [releaseMessageNavigationScroll, setSuppressBottomScroll])

  const scheduleBottomScrollCallback = React.useCallback((callback: () => void, delay: number) => {
    const timerId = setTimeout(() => {
      bottomScrollTimerIdsRef.current = bottomScrollTimerIdsRef.current.filter(id => id !== timerId)
      callback()
    }, delay)
    bottomScrollTimerIdsRef.current.push(timerId)
  }, [])

  const isConversationBottomSnapRequested = React.useCallback(
    () => conversationBottomSnapRequestedRef.current,
    []
  )

  const clearConversationBottomSnapRequest = React.useCallback(() => {
    conversationBottomSnapRequestedRef.current = false
  }, [])

  /** Scroll container to the absolute bottom, then align the end sentinel. */
  const scrollEndIntoView = React.useCallback((behavior: ScrollBehavior) => {
    if (suppressBottomScrollRef.current) {
      return
    }

    try {
      const container = messagesContainerRef.current
      if (container) {
        const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight)
        if (behavior === 'auto') {
          container.scrollTop = maxScrollTop
        } else {
          container.scrollTo({ top: maxScrollTop, behavior })
        }
      }

      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to scroll to bottom:', error)
    }
  }, [])

  const scrollToBottom = React.useCallback(() => {
    if (suppressBottomScrollRef.current) {
      return
    }

    const performScroll = () => scrollEndIntoView('smooth')

    requestAnimationFrame(() => {
      performScroll()
      scheduleBottomScrollCallback(performScroll, 100)
      scheduleBottomScrollCallback(performScroll, 300)
    })
  }, [scheduleBottomScrollCallback, scrollEndIntoView])

  const scrollToBottomInstant = React.useCallback(() => {
    if (suppressBottomScrollRef.current) {
      return
    }

    const performScroll = () => scrollEndIntoView('auto')

    try {
      performScroll()

      requestAnimationFrame(() => {
        performScroll()
        requestAnimationFrame(performScroll)
      })

      scheduleBottomScrollCallback(performScroll, 50)
      scheduleBottomScrollCallback(performScroll, 150)
      scheduleBottomScrollCallback(performScroll, 300)
      scheduleBottomScrollCallback(performScroll, 500)
      scheduleBottomScrollCallback(performScroll, 800)

      const container = messagesContainerRef.current
      if (container) {
        const mediaElements = Array.from(container.querySelectorAll('img, video'))
        mediaElements.forEach(element => {
          const isAlreadyLoaded = element instanceof HTMLImageElement ? element.complete : false
          if (isAlreadyLoaded) return

          const handleMediaLoad = () => {
            if (suppressBottomScrollRef.current) {
              return
            }

            const current = messagesContainerRef.current
            if (!current) return
            const distanceFromBottom = current.scrollHeight - current.scrollTop - current.clientHeight
            if (distanceFromBottom < 150) {
              scrollEndIntoView('auto')
            }
          }

          element.addEventListener('load', handleMediaLoad, { once: true })
          element.addEventListener('loadeddata', handleMediaLoad, { once: true })
          element.addEventListener('error', handleMediaLoad, { once: true })
        })
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to scroll to bottom instantly:', error)
    }
  }, [scheduleBottomScrollCallback, scrollEndIntoView])

  const prepareConversationBottomScroll = React.useCallback(() => {
    if (messageNavigationReleaseTimerRef.current) {
      clearTimeout(messageNavigationReleaseTimerRef.current)
      messageNavigationReleaseTimerRef.current = null
    }
    suppressBottomScrollRef.current = false
    conversationBottomSnapRequestedRef.current = true
    scrollToBottomInstant()
  }, [scrollToBottomInstant])

  const maintainScrollPosition = React.useCallback(() => {
    try {
      cancelBottomScrollTimers()
      if (loadPreviousSuppressReleaseRef.current) {
        clearTimeout(loadPreviousSuppressReleaseRef.current)
        loadPreviousSuppressReleaseRef.current = null
      }
      suppressBottomScrollRef.current = true

      const container = messagesContainerRef.current
      if (!container) {
        return
      }

      scrollPositionRef.current = {
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
      }

      scrollAnchorRef.current = null
      const containerRect = container.getBoundingClientRect()
      const messageElements = Array.from(container.querySelectorAll('[data-message-id]'))

      for (const element of messageElements) {
        const htmlElement = element as HTMLElement
        const rect = htmlElement.getBoundingClientRect()
        if (rect.bottom <= containerRect.top || rect.top >= containerRect.bottom) {
          continue
        }

        const messageId = Number(htmlElement.getAttribute('data-message-id'))
        if (!Number.isFinite(messageId)) {
          continue
        }

        scrollAnchorRef.current = {
          messageId,
          offsetTop: rect.top - containerRect.top,
        }
        break
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to maintain scroll position:', error)
    }
  }, [cancelBottomScrollTimers])

  const restoreScrollPosition = React.useCallback(() => {
    try {
      const container = messagesContainerRef.current
      if (!container) {
        return
      }

      const anchor = scrollAnchorRef.current
      if (anchor) {
        const anchorElement = container.querySelector(
          `[data-message-id="${anchor.messageId}"]`
        ) as HTMLElement | null

        if (anchorElement) {
          const containerRect = container.getBoundingClientRect()
          const rect = anchorElement.getBoundingClientRect()
          const offsetTop = rect.top - containerRect.top
          container.scrollTop += offsetTop - anchor.offsetTop
        }
      } else if (scrollPositionRef.current) {
        const { scrollTop, scrollHeight: oldHeight } = scrollPositionRef.current
        const newHeight = container.scrollHeight

        if (newHeight > oldHeight) {
          container.scrollTop = scrollTop + (newHeight - oldHeight)
        }
      }

      scrollAnchorRef.current = null
      scrollPositionRef.current = null

      loadPreviousSuppressReleaseRef.current = setTimeout(() => {
        suppressBottomScrollRef.current = false
        loadPreviousSuppressReleaseRef.current = null
      }, 250)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to restore scroll position:', error)
      suppressBottomScrollRef.current = false
    }
  }, [])

  const scrollToMessageInContainer = React.useCallback(
    (messageId: number, behavior: ScrollBehavior = 'auto'): boolean => {
      const container = messagesContainerRef.current
      if (!container) return false

      try {
        return scrollMessageIntoContainerView(container, messageId, behavior)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to scroll to message:', error)
        return false
      }
    },
    []
  )

  const scrollToMessageWithRetries = React.useCallback(
    (messageId: number, behavior: ScrollBehavior = 'auto') => {
      beginMessageNavigationScroll()

      const attempt = () => scrollToMessageInContainer(messageId, behavior)
      attempt()
      requestAnimationFrame(attempt)
      scheduleBottomScrollCallback(attempt, 100)
      scheduleBottomScrollCallback(attempt, 300)
      scheduleBottomScrollCallback(attempt, 600)
      scheduleBottomScrollCallback(attempt, 900)

      const container = messagesContainerRef.current
      if (container) {
        scheduleMessageMediaLoadRecenter(container, messageId, behavior, scrollToMessageInContainer)
      }

      releaseMessageNavigationScroll()
    },
    [
      beginMessageNavigationScroll,
      releaseMessageNavigationScroll,
      scheduleBottomScrollCallback,
      scrollToMessageInContainer,
    ]
  )

  const scrollToMessage = React.useCallback(
    (messageId: number) => {
      scrollToMessageWithRetries(messageId, 'smooth')
    },
    [scrollToMessageWithRetries]
  )

  const scrollToMessageWithDelay = React.useCallback(
    (messageId: number, delay = 500) => {
      beginMessageNavigationScroll()
      scheduleBottomScrollCallback(() => {
        scrollToMessageWithRetries(messageId, 'auto')
      }, delay)
    },
    [beginMessageNavigationScroll, scheduleBottomScrollCallback, scrollToMessageWithRetries]
  )

  const scrollToConversation = React.useCallback((conversationId: number, conversationType?: string) => {
    const attemptScroll = () => {
      try {
        if (conversationListRef.current) {
          if (conversationType) {
            let targetTab = 'direct'
            if (conversationType === 'group' || conversationType === 'course' || conversationType === 'private_group') {
              targetTab = 'group'
            } else if (conversationType === 'community') {
              targetTab = 'community'
            }

            const tabButton = conversationListRef.current.querySelector(`[data-tab="${targetTab}"]`) as HTMLElement
            if (tabButton) {
              tabButton.click()
            }
          }

          const conversationElement = conversationListRef.current.querySelector(
            `[data-conversation-id="${conversationId}"]`
          ) as HTMLElement

          if (conversationElement) {
            conversationElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            conversationElement.classList.add('bg-blue-50', 'border-blue-200')
            setTimeout(() => {
              conversationElement.classList.remove('bg-blue-50', 'border-blue-200')
            }, 2000)
            return true
          }

          return false
        }

        return false
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to scroll to conversation:', error)
        return false
      }
    }

    if (!attemptScroll()) {
      scheduleBottomScrollCallback(() => {
        if (!attemptScroll()) {
          scheduleBottomScrollCallback(attemptScroll, 500)
        }
      }, 100)
    }
  }, [scheduleBottomScrollCallback])

  React.useEffect(() => {
    return () => {
      cancelBottomScrollTimers()
      if (messageNavigationReleaseTimerRef.current) {
        clearTimeout(messageNavigationReleaseTimerRef.current)
      }
      if (loadPreviousSuppressReleaseRef.current) {
        clearTimeout(loadPreviousSuppressReleaseRef.current)
      }
    }
  }, [cancelBottomScrollTimers])

  return {
    messagesEndRef,
    messagesContainerRef,
    conversationListRef,
    scrollToBottom,
    scrollToBottomInstant,
    setSuppressBottomScroll,
    beginMessageNavigationScroll,
    isBottomScrollSuppressed,
    isNearBottom,
    maintainScrollPosition,
    scrollToMessage,
    scrollToMessageWithRetries,
    scrollToMessageWithDelay,
    scrollToConversation,
    restoreScrollPosition,
    prepareConversationBottomScroll,
    isConversationBottomSnapRequested,
    clearConversationBottomSnapRequest,
  }
}
